

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/Auth';
import {
  Card,
  Grid,
  CircularProgress,
  Icon,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import { formatDate } from 'utils/DateTimeConversion';
import OrderModal from 'components/CustomModals/AddOrderModal';
import { Link } from 'react-router-dom'; // Import Link from react-router-dom
import DataLoadingSpinner from "components/Spinners/DataLoadingSpinner";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { usePermission } from 'auth/usePermission';
import StatusBadge from 'components/StatusBadge';
import MonoText from 'components/MonoText';
import TableActionMenu from 'components/TableActionMenu';
import { frontendLogger } from '../../utils/logger';
import useOpenCreateFromUrl from 'hooks/useOpenCreateFromUrl';

const capitalizeFirstLetter = (str) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

function Orders() {
  const { user, axiosInstance } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  // const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const { canCreate, isSupportUser } = usePermission('orders');
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [newOrder, setNewOrder] = useState({
    // deal_id: id,
    total_amount: 0,
    status: 'Sent',
  })

  const handleCreateNewOrder = () => {
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to create orders.',
        severity: 'error',
      });
      return; // Exit the function early
    }
    setIsOrderModalOpen(true); // Open the modal
  };
  // v2 header "New order": the same dialog as Add Order, for users who may create.
  useOpenCreateFromUrl(handleCreateNewOrder, { allowed: canCreate && !isSupportUser });
  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axiosInstance.get('/users/');
        setUsers(response.data);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchUsers();
  }, []);

  // useEffect(() => {
  //   if (user) {
  //     const currentUserAsAgent = {
  //       id: user.id,
  //       username: user.username
  //     };
  //     setSelectedAgent(currentUserAsAgent);
  //   }

  // }, [user]);


  const handleSaveOrder = async (data) => {

    // Generate a unique order name
    const uniqueOrderName = `ORD-${Date.now()}`; // You can also use a UUID if preferred
    //   if (!user) {
    //     console.error("User context is not available.");
    //     return;
    // }
    // Prepare the tax slabs and amounts from the tax summary
    const taxSummary = data.tax_summary || [];
    const taxSlabs = taxSummary.map(item => item.taxRate); // Extract tax rates (e.g., [5, 10, 18])
    const taxAmounts = taxSummary.map(item => item.amount); // Extract tax amounts (e.g., [1223, 456, 789])
    const customFields = {};
    Object.keys(data).forEach(key => {
      if (key.startsWith('cf')) {
        customFields[key] = data[key];
      }
    });
    // Prepare the main order data with tax slabs and amounts
    const orderData = {
      deal_id: data.deal_id, // Assuming 'id' is the deal ID for the order
      quotation_id: data.quotation_id,
      account_id: data.account_id,
      account_name: data.account_name,
      contact_id: data.contact_id,
      deal_name: data.deal_name, // Include deal name
      // order_number: uniqueOrderName, // Set the unique order name
      total_amount: data.total_amount,
      grand_total: data.total_amount,
      status: data.status,
      notes: data.notes,
      shipping_method: data.shipping_method,
      shipping_instruction: data.shipping_instruction,
      // payment_methods: data.payment_methods,
      // order_date: data.order_date,
      tax: data.tax,
      delivery_date: data.delivery_date || null,
      subtotal: data.subtotal, // Include subtotal
      // Add tax slabs and amounts directly into the order data
      tax_slab_1: data.tax_slab_1 || taxSlabs[0] || null,
      tax_slab_2: data.tax_slab_2 || taxSlabs[1] || null,
      tax_slab_3: data.tax_slab_3 || taxSlabs[2] || null,
      tax_slab_4: data.tax_slab_4 || taxSlabs[3] || null,
      tax_slab_5: data.tax_slab_5 || taxSlabs[4] || null,
      tax_slab_amount_1: data.tax_slab_amount_1 || taxAmounts[0] || null,
      tax_slab_amount_2: data.tax_slab_amount_2 || taxAmounts[1] || null,
      tax_slab_amount_3: data.tax_slab_amount_3 || taxAmounts[2] || null,
      tax_slab_amount_4: data.tax_slab_amount_4 || taxAmounts[3] || null,
      tax_slab_amount_5: data.tax_slab_amount_5 || taxAmounts[4] || null,
      order_date: new Date().toISOString(),
      created_by: data.user.id,
      created_by_name: data.user.username,
      updated_by: data.user.id,
      updated_by_name: data.user.username,
      shipping_address: data.shipping_address,
      billing_address: data.billing_address,
      po_number: data.po_number,
      currency: data.currency,
      transid: data.transid,
      transname: data.transname,
      transmode: data.transmode,
      vehno: data.vehno,
      vehtype: data.vehtype,
      ...customFields,
      // Also include in custom_fields object for API compatibility
      custom_fields: customFields
    };

    // console.log('New Order Data:', orderData); // Log the order data being saved

    // Check for missing fields
    const missingFields = [];
    for (const [key, value] of Object.entries(orderData)) {
      if (value === null || value === undefined) {
        missingFields.push(key);
      }
    }
    if (missingFields.length > 0) {
      console.warn('Missing fields in order data:', missingFields);
    }

    setIsLoading(true);
    handleCloseOrderModal();

    try {
      // Create the order
      const orderResponse = await axiosInstance.post('/orders/', orderData);
      const newOrderId = orderResponse.data.id;

      // console.log('Order created with ID:', newOrderId); // Log the new order ID

      // Add order_id to each item
      const orderItems = data.items.map((item) => ({
        order_id: newOrderId, // Attach the order_id
        product_name: item.product_name,
        product_id: item.product_id,
        quantity: item.quantity,
        unit_price: parseFloat(item.price), // Ensure numerical fields are properly formatted
        total_price: parseFloat(item.total_price),
        discount_percentage: item.discount_percentage || null,
        tax: item.tax || null,
        discounted_price: item.discounted_price || null,
      }));


      // console.log('Order Items before saving:', orderItems); // Log the order items being saved

      // Create the order items
      await Promise.all(
        orderItems.map((item) =>
          axiosInstance.post('/orders/items/', item)
        )
      );
      // Fetch the updated order with items
      const updatedOrderResponse = await axiosInstance.get(`/orders/${newOrderId}`);

      await fetchOrders();

      setSnackbar({
        open: true,
        message: 'Order created successfully!',
        severity: 'success',
      });

      navigate(`/orders/${newOrderId}`); // Redirect to the new order detail page

      // --- Log order creation ---
      await frontendLogger.logCreate('Order', {
        id: newOrderId,
        action: 'create',
        account_id: orderData.account_id,
        account_name: orderData.account_name,
        total_amount: orderData.total_amount,
        created_by: user.id,
        created_at: orderResponse.data.created_at || new Date().toISOString()
      });

    } catch (error) {
      console.error('Error saving order:', error);
      if (error.response) {
        console.error('Response data:', error.response.data); // Log the response data for more details
        console.error('Response status:', error.response.status); // Log the response status
      }
      setSnackbar({
        open: true,
        message: `Error saving order: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };


  const handleCloseOrderModal = () => {
    setIsOrderModalOpen(false);
    setNewOrder({
      // deal_id: id,
      total_amount: '',
    })
  }

  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get('/orders/');
      const orders = response.data;

      // Simplified account name fetching
      const ordersWithAccountInfo = orders.map(order => ({
        ...order,
        account_name: order.account_name || '', // Use existing account_name or fallback
        account_id: order.account_id || null
      }));

      // Sort orders by created_at in descending order
      const sortedOrders = ordersWithAccountInfo.sort((a, b) => {
        const dateA = new Date(a.created_at); // Use created_at for sorting
        const dateB = new Date(b.created_at);
        return dateB - dateA; // Sort in descending order
      });

      console.log('Fetched orders:', sortedOrders); // Debug log
      setOrders(sortedOrders);
      setFilteredOrders(sortedOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // if (isLoading) {
  //   return (
  //     <DashboardLayout>
  //       <DashboardNavbar />
  //       <DataLoadingSpinner 
  //         title="Loading Order Data"
  //         message="Please wait while we fetch the orders information..."
  //       />
  //       
  //     </DashboardLayout>
  //   );
  // }
  const handleRowClick = (orderId) => {
    navigate(`/orders/${orderId}`);
  };
  function formatOrderDate(dateString) {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    const formattedDate = new Date(dateString).toLocaleDateString('en-US', options);
    return formattedDate;
  }

  const columns = [
    // { Header: "ID", accessor: "id", width: "5%" },
    {
      Header: "Order ID",
      accessor: "order_number",
      width: "15%",
      Cell: ({ value, row }) => (
        <MonoText>
          <MDTypography
            component="span"
            variant="caption"
            color="info"
            fontWeight="medium"
            sx={{ cursor: 'pointer' }}
            onClick={(e) => {
              e.preventDefault();
              handleRowClick(row.original.id);
            }}
          >
            {capitalizeFirstLetter(value) || `Order #${row.original.id}`}
          </MDTypography>
        </MonoText>
      ),
    },
    {
      Header: "Order Creation Date",
      accessor: "created_at",
      width: "15%",
      Cell: ({ value }) => (
        <MonoText>{value ? formatOrderDate(value) : "-"}</MonoText>
      ),
    },
    {
      Header: "Status",
      accessor: "status",
      width: "12%",
      Cell: ({ value }) => <StatusBadge status={value} label={capitalizeFirstLetter(value)} />,
    },
    {
      Header: "Account",
      accessor: "account_name",
      width: "18%",
      Cell: ({ row }) => {
        const accountId = row.original.account_id;
        const accountName = row.original.account_name;

        return accountId ? (
          <MDTypography
            component={Link}
            to={`/accounts/${accountId}`}
            variant="caption"
            color="info"
            fontWeight="medium"
            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
          >
            {capitalizeFirstLetter(accountName || '')}
          </MDTypography>
        ) : (
          <MDTypography
            variant="caption"
            color="text"
            fontWeight="medium"
          >
            {capitalizeFirstLetter(accountName || '') || '-'}
          </MDTypography>
        );
      },
    },
    {
      Header: "Created By",
      accessor: "created_by_name",
      width: "15%",
      Cell: ({ value }) => (
        <MDTypography variant="caption">
          {capitalizeFirstLetter(value) || '-'}
        </MDTypography>
      ),
    },
    {
      Header: "Modified Date",
      accessor: "updated_at",
      width: "20%",
      Cell: ({ value }) => <MonoText>{value ? formatDate(value) : '-'}</MonoText>,
    },
    {
      Header: "Actions",
      accessor: "actions",
      width: "5%",
      align: "center",
      disableSortBy: true,
      disableExport: true,
      Cell: ({ row }) => (
        <TableActionMenu
          row={row.original}
          onView={(r) => handleRowClick(r.id)}
        />
      ),
    },
  ];


  // if (isLoading) {
  //   return (
  //     <DashboardLayout>
  //       <DashboardNavbar />
  //       <MDBox display="flex" justifyContent="center" alignItems="center" height="80vh">
  //         <CircularProgress />
  //       </MDBox>
  //       
  //     </DashboardLayout>
  //   );
  // }

  const handleProductsChange = (updatedProducts) => {
    setSelectedProducts(updatedProducts);
    console.log('Updated products:', updatedProducts);
  };

  const filterOrders = (criteria) => {
    const filtered = orders.filter(order => {
      // Implement your filtering logic based on criteria
      return true; // Replace with actual condition
    });
    setFilteredOrders(filtered);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDBox display="flex" justifyContent="space-between" alignItems="center" width="100%">
                  <MDTypography variant="h6" color="white">
                    Orders
                  </MDTypography>
                  <MDBox>
                    <MDButton
                      variant="gradient"
                      color="dark"
                      onClick={handleCreateNewOrder}
                      sx={{ marginRight: 2 }}
                    >
                      <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                      &nbsp;Add Order
                    </MDButton>
                  </MDBox>
                </MDBox>

              </MDBox>
              <MDBox pt={3}>
                {isLoading ? (
                  <TableSkeleton />
                ) : (
                  <>
                    {filteredOrders.length > 0 ? (
                      <DataTable
                        table={{ columns, rows: filteredOrders }}
                        isSorted={true}
                        entriesPerPage={{
                          defaultValue: 10,
                          entries: [5, 10, 15, 20, 25],
                        }}
                        showTotalEntries={true}
                        noEndBorder
                        canSearch={true}
                        searchPlaceholder="Search Orders"
                        defaultSortField="created_at"
                        defaultSortDirection="desc"
                        pagination={{ variant: "gradient", color: "info" }}
                        exportCSV={true}
                        objectType="order"
                        onFilterApply={(conditions) => {
                          console.log('Filter applied:', conditions);
                        }}
                        onFilterClear={() => {
                          console.log('Filter cleared');
                          fetchOrders();
                        }}
                      />
                    ) : (
                      <EmptyState
                        moduleName="Order"
                        canCreate={canCreate}
                      />
                    )}
                  </>
                )}
              </MDBox>

            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <OrderModal
        isOpen={isOrderModalOpen}
        onClose={() => setIsOrderModalOpen(false)}
        onSave={handleSaveOrder}
        onProductsChange={handleProductsChange}
        user={user}
      />
      
    </DashboardLayout>
  );
}

export default Orders;